import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dashboard, OffplanPdfManager } from '@/components';
import { newBuildingsProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { NotificationTypeEnum, CreateNewPlanType, NewBuildingModel } from '@/types';
import { IShortPlan, IUnit } from '@/types/offplans';
import { IUnitSearchQueryParams } from '@/types/requests';

interface IUnitData {
  units: IUnit[];
  total: number;
}
interface IPlanData {
  plans: any[];
  total: number;
}

const extractIdsFromString = (queryParams: URLSearchParams): number[] =>
  queryParams.getAll('ids[]').map((id) => parseInt(id, 10));

// const extractBuildingIdFromUrl = (queryParams: URLSearchParams): number[] =>
//   queryParams.getAll('buildingIds[]').map(id => parseInt(id, 10));

const getUnits = async (offplanId: string, dispatch: any): Promise<any> => {
  try {
    const { ok, data, status, message } = await newBuildingsProvider.getAllUnitsByOffplanId(offplanId);
    if (ok && data && data.items && data.items.length > 0) {
      // console.log(data)
      return { units: data.items, total: data.items.length };
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
  } catch (error) {
    console.error(error);
    dispatch(appSetNotification(NotificationTypeEnum.Error, 'An unexpected error occurred', 500));
  }
};

const getPlans = async (offplanId: string, dispatch: any): Promise<IPlanData | undefined> => {
  try {
    const { ok, data, status, message } = await newBuildingsProvider.getPlans(offplanId);
    if (ok && data && data?.items && data?.items.length > 0) {
      const items = data.items as any[];
      const tPlans = items.filter((el) => el?.units && el.units.length > 0);

      return { plans: tPlans, total: tPlans.length };
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
  } catch (error) {
    console.error(error);
    dispatch(appSetNotification(NotificationTypeEnum.Error, 'An unexpected error occurred', 500));
  }
};

const getOffPlanData = async (offplanId: string, dispatch: any) => {
  const { ok, data, status, message } = await newBuildingsProvider.getOffPlanById(offplanId!);
  if (ok && data) {
    return data;
  } else {
    dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
  }
};

const filterUniquePlans = (units: IUnit[]): IShortPlan[] => {
  const uniqueCombinationMap = new Map<string, IShortPlan>();

  units.forEach(unit => {
    const combinationKey = `${unit.beds}-${unit.planCode}-${unit.planId}`;

    if (!uniqueCombinationMap.has(combinationKey)) {
      const shortPlan: IShortPlan = {
        beds: unit.beds,
        planCode: unit.planCode,
        planId: unit.planId,
        media: unit.media,
      };

      uniqueCombinationMap.set(combinationKey, shortPlan);
    }
  });

  return Array.from(uniqueCombinationMap.values());
};

export const OffplanPdfManagerPage = () => {
  const { id: offplanId } = useParams();
  const dispatch = useDispatch();
  const [unitsData, setUnitsData] = useState<IUnitData>({ units: [], total: 0 });
  const [plansData, setPlansData] = useState<IPlanData>({ plans: [], total: 0 });
  const [offplanData, setOffplanData] = useState<NewBuildingModel>();

  // const queryParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    const fetchData = async () => {
      let tUnits: IUnit[] = [];
      if (offplanId) {
        const resUnits = await getUnits(offplanId, dispatch);

        if (resUnits) {
          const { units, total } = resUnits;
          tUnits = units;
          setUnitsData({ units, total });
        }
      }

      if (offplanId) {
        const resPlans = await getPlans(offplanId, dispatch);
        const { plans, total } = resPlans || {};
        if (plans && total) {
          setPlansData({ plans, total });
        }
      } 


      if (offplanId) {
        const resOffplanData = await getOffPlanData(offplanId, dispatch);
        if (resOffplanData) {
          setOffplanData(resOffplanData);
        }
      }
    };

    fetchData();
  }, []);

  return (
    <Dashboard openLogIn>
      {offplanId && (
        <OffplanPdfManager
          offplanId={offplanId}
          offplanData={offplanData}
          units={unitsData.units}
          totalUnits={unitsData.total}
          plans={plansData.plans}
          totalPlans={plansData.total}
        />
      )}
    </Dashboard>
  );
};
